/**
 * Passation de mandat du maire — M21 (retour Manon, mi-parcours).
 *
 * « Le maire peut être remplacé après la première inscription (ou rétrogradé
 *   manager), à condition qu'un admin référent soit nommé : jamais de commune
 *   sans admin. » Cas réel : les élections municipales de 2026.
 *
 * En une fois : le nouveau maire devient administrateur complet, son nom
 * s'imprime sur les actes, l'ancien devient manager (ou reste admin, ou est
 * désactivé). Le serveur refuse tout ce qui laisserait la commune sans
 * administrateur complet. Réservé à un administrateur complet.
 */
import { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import { Card, Button, Input } from '@/components/ui';
import { apiFetch, api, ApiException } from '@/lib/api';
import type { AgentAnnuaire } from '@roronoa-collectivites/shared-types';

interface Situation { maireNom: string | null; maireTitre: string | null; adminsComplets: Array<{ id: string; nom: string }> }

export function PassationMandatCard() {
  const [sit, setSit] = useState<Situation | null>(null);
  const [personnel, setPersonnel] = useState<AgentAnnuaire[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [nouveau, setNouveau] = useState(''); const [nom, setNom] = useState(''); const [titre, setTitre] = useState('Maire');
  const [ancien, setAncien] = useState(''); const [devient, setDevient] = useState<'manager' | 'admin' | 'agent' | 'desactive'>('manager');
  const [confirme, setConfirme] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);

  const charger = () => apiFetch<Situation>('/api/admin/gouvernance').then((s) => {
    setSit(s); setAncien(s.adminsComplets[0]?.id ?? '');
  }).catch(() => setSit(null));
  useEffect(() => {
    void charger();
    api.annuaireInterne.lister().then((r) => setPersonnel(r.agents)).catch(() => setPersonnel([]));
  }, []);

  async function valider() {
    setMsg(null);
    try {
      await apiFetch('/api/admin/gouvernance/passation', { method: 'POST', body: JSON.stringify({
        nouveauMaireUserId: nouveau, maireNom: nom.trim(), maireTitre: titre.trim() || null,
        ancienMaireUserId: ancien || null, ancienDevient: devient,
      }) });
      setMsg({ ok: true, t: 'Passation effectuée. Le nouveau nom figure désormais sur les actes.' });
      setOuvert(false); setConfirme(false); await charger();
    } catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Passation impossible' }); }
  }

  if (!sit) return null;
  return (
    <Card variant="glass" padded className="mt-4">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink">
        <Landmark className="h-4 w-4" aria-hidden="true" /> Maire et passation de mandat
      </h2>
      <p className="text-xs text-ink-muted">
        Maire en titre : <b className="text-ink">{sit.maireNom ?? 'non renseigné'}</b>{sit.maireTitre ? ` (${sit.maireTitre})` : ''} ·
        administrateur(s) complet(s) : {sit.adminsComplets.map((a) => a.nom).join(', ') || '—'}
      </p>
      {msg && <p className={`mt-2 text-xs ${msg.ok ? 'text-success' : 'text-urgent'}`} role={msg.ok ? 'status' : 'alert'}>{msg.t}</p>}
      {!ouvert ? (
        <Button size="sm" variant="glass" className="mt-2" onClick={() => setOuvert(true)}>Organiser une passation (élections)</Button>
      ) : (
        <div className="mt-3 space-y-2">
          <label className="block text-xs text-ink">Nouveau maire (compte existant du personnel)
            <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={nouveau}
              onChange={(e) => { setNouveau(e.target.value); const p = personnel.find((x) => x.id === e.target.value); if (p) setNom(`${p.prenom} ${p.nom}`); }}>
              <option value="">— Choisir —</option>
              {personnel.map((p) => <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs text-ink">Nom imprimé sur les actes
              <Input className="mt-1" value={nom} maxLength={200} onChange={(e) => setNom(e.target.value)} />
            </label>
            <label className="block text-xs text-ink">Qualité
              <Input className="mt-1" value={titre} maxLength={100} onChange={(e) => setTitre(e.target.value)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs text-ink">Maire sortant
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={ancien} onChange={(e) => setAncien(e.target.value)}>
                <option value="">— Aucun changement —</option>
                {sit.adminsComplets.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </label>
            <label className="block text-xs text-ink">Il devient
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={devient}
                onChange={(e) => setDevient(e.target.value as typeof devient)} disabled={!ancien}>
                <option value="manager">Manager</option>
                <option value="agent">Agent</option>
                <option value="admin">Reste administrateur</option>
                <option value="desactive">Compte désactivé</option>
              </select>
            </label>
          </div>
          <label className="flex items-start gap-2 text-xs text-ink">
            <input type="checkbox" className="mt-0.5" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
            Je confirme la passation. La commune garde toujours au moins un administrateur complet : le serveur refusera sinon.
          </label>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={!nouveau || nom.trim().length < 2 || !confirme} onClick={() => void valider()}>Valider la passation</Button>
            <Button size="sm" variant="ghost" onClick={() => { setOuvert(false); setConfirme(false); }}>Annuler</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
